import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Button, Badge } from 'react-bootstrap';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const PublicSchoolInfo = () => {
  const [generalInfo, setGeneralInfo] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState({ general: true, documents: true });
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading({ general: true, documents: true });
      setError('');

      // Fetch General Information
      const generalResponse = await fetch(`${BASE_URL}/school_info.php?type=general`);
      const generalResult = await generalResponse.json();
      
      if (generalResult.success) {
        setGeneralInfo(generalResult.data || []);
      }

      // Fetch Public Documents
      const documentsResponse = await fetch(`${BASE_URL}/school_info.php?type=documents`);
      const documentsResult = await documentsResponse.json();
      
      if (documentsResult.success) {
        const activeDocs = (documentsResult.data || []).filter(doc => 
          doc.is_active === "1" || doc.is_active === 1 || doc.is_active === true
        ).sort((a, b) => a.display_order - b.display_order);
        setDocuments(activeDocs);
      }

    } catch (error) {
      console.error('Fetch error:', error);
      setError('Error loading information');
    } finally {
      setLoading({ general: false, documents: false });
    }
  };

  const getFileUrl = (filePath) => {
    if (!filePath) return null;
    return `${UPLOAD_BASE_URL}${filePath}`;
  };

  const handleRetry = () => {
    fetchData();
  };

  // Group documents into rows of 3 items for better display
  const groupDocuments = (docs, itemsPerRow = 3) => {
    const groups = [];
    for (let i = 0; i < docs.length; i += itemsPerRow) {
      groups.push(docs.slice(i, i + itemsPerRow));
    }
    return groups;
  };

  if (loading.general && loading.documents) {
    return (
      <>
        <div style={{ height: '0px' }}></div>
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading School Information...</p>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div style={{ height: '0px' }}></div>
        <Container className="py-5">
          <Alert variant="danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <div className="mt-2">
              <Button 
                variant="outline-primary"
                size="sm"
                onClick={handleRetry}
              >
                <i className="fas fa-redo me-1"></i>
                Try Again
              </Button>
            </div>
          </Alert>
        </Container>
      </>
    );
  }

  const documentGroups = groupDocuments(documents, 3);

  return (
    <>
      <Container className="py-5">
        {/* Header */}
        <Row className="mb-4">
          <Col className="text-center">
            <h1 className="display-5 fw-bold text-primary">Mandatory Public Disclosure</h1>
            <p className="lead text-muted">
              Public Disclosure
            </p>
            <hr className="my-4" />
          </Col>
        </Row>

        {/* Section A: General Information */}
        <Row className="mb-5">
          <Col>
            <Card className="border-primary shadow-sm">
              <Card.Header className="bg-primary text-white">
                <h4 className="mb-0">
                  <i className="fas fa-school me-2"></i>
                  A. General Information
                  <Badge bg="light" text="dark" className="ms-2">
                    {generalInfo.length} Items
                  </Badge>
                </h4>
              </Card.Header>
              <Card.Body>
                {generalInfo.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th width="80" className="text-center">Sl No.</th>
                          <th width="250">Title</th>
                          <th>Information</th>
                        </tr>
                      </thead>
                      <tbody>
                        {generalInfo.map((info) => (
                          <tr key={info.sl_no}>
                            <td className="text-center">
                              <Badge bg="primary" className="fs-6">{info.sl_no}</Badge>
                            </td>
                            <td>
                              <strong>{info.title}</strong>
                            </td>
                            <td>{info.information}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <Alert variant="info" className="mb-0">
                    <i className="fas fa-info-circle me-2"></i>
                    General information will be updated soon.
                  </Alert>
                )}
              </Card.Body>
              <Card.Footer className="text-muted small">
                <i className="fas fa-clock me-1"></i>
                Information as per latest records
              </Card.Footer>
            </Card>
          </Col>
        </Row>

        {/* Section B: Documents and Information - Grid View */}
        <Row className="mb-5">
          <Col>
            <Card className="border-success shadow-sm">
              <Card.Header className="bg-success text-white">
                <h4 className="mb-0">
                  <i className="fas fa-file-alt me-2"></i>
                  B. Documents and Information
                  <Badge bg="light" text="dark" className="ms-2">
                    {documents.length} Documents
                  </Badge>
                </h4>
              </Card.Header>
              <Card.Body className="py-4">
                {documents.length > 0 ? (
                  <div className="documents-grid-container">
                    {documentGroups.map((group, groupIndex) => (
                      <div key={groupIndex} className="mb-5">
                        {/* Document Row */}
                        <Row>
                          <Col xs={12}>
                            <div className="d-flex justify-content-center">
                              {group.map((doc) => (
                                <div key={doc.doc_id} className="text-center mx-3" style={{ width: '140px' }}>
                                  <div className="document-card">
                                   
                                    
                                    {/* Large PDF Icon */}
                                    <div className="pdf-icon-container mb-3">
                                      <i className="fas fa-file-pdf pdf-icon"></i>
                                    </div>
                                     {/* File Name on Top */}
                                    <div className="document-name p-2 mb-2">
                                      <strong className="small">{doc.doc_name}</strong>
                                    </div>
                                    {/* View PDF Button */}
                                    <div className="document-actions">
                                      {doc.file_path ? (
                                        <Button 
                                          variant="danger" 
                                          size="sm"
                                          href={getFileUrl(doc.file_path)}
                                          target="_blank"
                                          className="w-100 pdf-button"
                                        >
                                          <i className="fas fa-eye me-1"></i>
                                          View PDF
                                        </Button>
                                      ) : (
                                        <Button 
                                          variant="secondary" 
                                          size="sm"
                                          disabled
                                          className="w-100"
                                        >
                                          <i className="fas fa-times me-1"></i>
                                          Coming Soon
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                              {group.length < 3 && Array.from({ length: 3 - group.length }).map((_, idx) => (
                                <div key={`empty-${idx}`} className="text-center mx-3" style={{ width: '140px', opacity: 0 }} />
                              ))}
                            </div>
                          </Col>
                        </Row>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Alert variant="info" className="mb-0">
                    <i className="fas fa-info-circle me-2"></i>
                    No public documents available at the moment.
                  </Alert>
                )}
              </Card.Body>
              <Card.Footer className="text-muted small d-flex justify-content-between align-items-center">
                <div>
                  <i className="fas fa-file-pdf me-1 text-danger"></i>
                  {documents.length} PDF Documents Available
                </div>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* CSS for Grid Layout */}
      <style jsx="true">{`
        .table th {
          font-weight: 600;
          background-color: #f8f9fa;
        }
        
        .table td {
          vertical-align: middle;
        }
        
        .card {
          border-radius: 10px;
        }
        
        .card-header {
          border-radius: 10px 10px 0 0 !important;
          font-weight: 600;
        }
        
        hr {
          border-top: 2px solid rgba(0, 0, 0, 0.1);
          width: 100px;
          margin: 20px auto;
        }
        
        /* Document Card Styles */
        .document-card {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          min-height: 200px;
        }
        
        .document-name {
          background-color: #f8f9fa;
          border-radius: 8px;
          border: 1px solid #dee2e6;
          min-height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          word-break: break-word;
          transition: all 0.3s ease;
        }
        
        .document-card:hover .document-name {
          background-color: #e9ecef;
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }
        
        /* Large PDF Icon Styles */
        .pdf-icon-container {
          width: 100px;
          height: 120px;
          background: linear-gradient(135deg, #ff6b6b, #ff8e8e);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
          box-shadow: 0 6px 15px rgba(255, 107, 107, 0.3);
          transition: all 0.3s ease;
        }
        
        .pdf-icon {
          font-size: 3.5rem;
          color: white;
        }
        
        .document-card:hover .pdf-icon-container {
          transform: scale(1.05);
          box-shadow: 0 8px 20px rgba(255, 107, 107, 0.4);
        }
        
        .document-actions .btn {
          font-size: 0.8rem;
          padding: 0.5rem 0.5rem;
          border-radius: 6px;
          font-weight: 500;
          letter-spacing: 0.5px;
        }
        
        .pdf-button {
          background-color: #dc3545;
          border-color: #dc3545;
          color: white;
          transition: all 0.3s ease;
        }
        
        .pdf-button:hover {
          background-color: #bb2d3b;
          border-color: #bb2d3b;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
        }
        
        .documents-grid-container {
          padding: 10px 0;
        }
        
        /* Responsive adjustments */
        @media (max-width: 992px) {
          .text-center.mx-3 {
            width: 130px !important;
          }
          
          .pdf-icon-container {
            width: 90px;
            height: 110px;
          }
          
          .pdf-icon {
            font-size: 3rem;
          }
        }
        
        @media (max-width: 768px) {
          .display-5 {
            font-size: 2rem;
          }
          
          .table-responsive {
            font-size: 0.9rem;
          }
          
          .text-center.mx-3 {
            margin-left: 15px !important;
            margin-right: 15px !important;
            width: 120px !important;
          }
          
          .document-name {
            font-size: 0.85rem;
          }
          
          .pdf-icon-container {
            width: 80px;
            height: 100px;
          }
          
          .pdf-icon {
            font-size: 2.5rem;
          }
        }
        
        @media (max-width: 576px) {
          .text-center.mx-3 {
            width: 110px !important;
            margin-left: 10px !important;
            margin-right: 10px !important;
          }
          
          .pdf-icon-container {
            width: 70px;
            height: 90px;
          }
          
          .pdf-icon {
            font-size: 2.2rem;
          }
          
          .document-actions .btn {
            font-size: 0.75rem;
            padding: 0.4rem 0.3rem;
          }
        }
        
        @media (max-width: 480px) {
          .text-center.mx-3 {
            width: 100px !important;
            margin-left: 5px !important;
            margin-right: 5px !important;
          }
          
          .pdf-icon-container {
            width: 60px;
            height: 80px;
          }
          
          .pdf-icon {
            font-size: 1.8rem;
          }
        }
      `}</style>
    </>
  );
};

export default PublicSchoolInfo;